import { useMemo } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { Customer } from '@/services/db/customer/entity';
import { getPeopleQueryOptions } from '@/services/db/customer/queries';
import { queryEncode } from '@/utils/condition-query-encoding';
import { isEmptyString } from '@/utils/string';
import { decodePeopleFiltersParam } from '@/pages/workspaces/people/components/people-filter-builder/filters-param';
import { normalizePeopleFilterWire } from '@/pages/workspaces/people/components/people-filter-builder/people-filter-format';
import { asJsonObject, type JsonValue } from '@/pages/workspaces/people/components/people-filter-builder/wire-types';
import { useListPicker, type ListFilterCodec, type ListPicker } from './use-list-picker';

const DEFAULT_PAGE_SIZE = 50;
/** The paginator's "unknown" reading, which is what it shows before the first load. */
const UNKNOWN_TOTAL = -1;

const peopleFilterCodec: ListFilterCodec<JsonValue> = {
  decode: (encoded) => normalizePeopleFilterWire(decodePeopleFiltersParam(encoded)),
  encode: (serialized) => {
    const envelope = asJsonObject(serialized);
    return envelope == null ? null : queryEncode(envelope);
  },
};

export type PeoplePicker = ListPicker<Customer, JsonValue> & {
  people: readonly Customer[];
  total: number;
  pageSize: number;
  isFetching: boolean;
  /** True once the reader has narrowed the list, which changes the empty state. */
  hasFilters: boolean;
};

/** The wizard's people list: narrowing, paging, selection, and the list read. */
export function usePeoplePicker(workspaceId: number): PeoplePicker {
  const picker = useListPicker<Customer, JsonValue>({ initialSelected: [], codec: peopleFilterCodec });
  const { search, filters, page } = picker.params;
  const queryParams = useMemo(
    () => ({
      page,
      email: filters == null && !isEmptyString(search) ? search : undefined,
      filters: filters ?? undefined,
      sort: 'created_at',
      asc: false,
    }),
    [page, search, filters],
  );
  // The list must always reflect the server, so it is never served from cache;
  // the previous page stays on screen while the next one loads.
  const query = useQuery({
    ...getPeopleQueryOptions(workspaceId, queryParams),
    staleTime: 0,
    placeholderData: keepPreviousData,
  });

  return {
    ...picker,
    people: query.data?.customers ?? [],
    total: query.data?.meta.pagination.total ?? UNKNOWN_TOTAL,
    pageSize: query.data?.meta.pagination.size ?? DEFAULT_PAGE_SIZE,
    isFetching: query.isFetching,
    hasFilters: filters != null || !isEmptyString(search),
  };
}
